import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore';
import { useTaskStore } from './taskStore';

export interface WorkspaceState {
  currentWorkspace: {
    type: 'personal' | 'team';
    teamId?: string;
    teamName?: string;
  };

  // Actions
  switchToPersonal: () => void;
  switchToTeam: (teamId: string, teamName?: string) => void;
  resetToPersonal: () => void;
  validateAndCleanWorkspace: () => void;
  getCurrentWorkspaceLabel: () => string;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      // 默认为个人空间
      currentWorkspace: { type: 'personal' },

      switchToPersonal: () => {
        set({ currentWorkspace: { type: 'personal' } });
        
        // 切换空间后，触发相关数据重新加载
        const taskStore = useTaskStore.getState();
        if (taskStore.loadTasks) {
          taskStore.loadTasks();
        }
        if (taskStore.loadTaskTypes) {
          taskStore.loadTaskTypes();
        }
        // 清理团队成员数据
        if (taskStore.clearTeamMembers) {
          taskStore.clearTeamMembers();
        }
      },

      switchToTeam: (teamId: string, teamName?: string) => {
        set({ 
          currentWorkspace: { 
            type: 'team', 
            teamId, 
            teamName 
          } 
        });
        
        // 切换空间后，触发相关数据重新加载
        const taskStore = useTaskStore.getState();
        if (taskStore.loadTasks) {
          taskStore.loadTasks();
        }
        if (taskStore.loadTaskTypes) {
          taskStore.loadTaskTypes();
        }
      },

      resetToPersonal: () => {
        set({ currentWorkspace: { type: 'personal' } });

        // 不触发数据重新加载，让调用方决定何时加载
      },

      validateAndCleanWorkspace: async () => {
        const { currentWorkspace } = get();

        // 只有团队工作空间需要验证
        if (currentWorkspace.type !== 'team' || !currentWorkspace.teamId) {
          return;
        }

        // 获取当前用户的团队列表并验证
        try {
          const { useAuthStore } = require('./authStore');
          const user = useAuthStore.getState().user;

          if (!user) {
            return;
          }

          if (!user.teamMemberships) {
            set({ currentWorkspace: { type: 'personal' } });
            return;
          }

          // 检查当前团队ID是否在用户的团队列表中
          const hasAccess = user.teamMemberships.some(
            membership => membership.team?.id === currentWorkspace.teamId
          );

          if (!hasAccess) {
            set({ currentWorkspace: { type: 'personal' } });
          }
        } catch (error) {
          // 静默处理验证错误，不要因为验证错误就切换到个人空间，可能是临时的网络问题
        }
      },

      getCurrentWorkspaceLabel: () => {
        const { currentWorkspace } = get();
        if (currentWorkspace.type === 'personal') {
          return '个人空间';
        } else {
          return currentWorkspace.teamName || '团队空间';
        }
      }
    }),
    {
      name: 'workspace-storage', // 持久化存储的key
      partialize: (state) => ({ currentWorkspace: state.currentWorkspace }), // 只持久化当前工作空间
    }
  )
);

// 获取当前工作空间的可用团队列表
export const useAvailableTeams = () => {
  const user = useAuthStore(state => state.user);
  
  if (!user || !user.teamMemberships) {
    return [];
  }
  
  return user.teamMemberships.map(membership => ({
    id: membership.team.id,
    name: membership.team.name,
    role: membership.role
  }));
};

// 工作空间相关的API参数构建辅助函数
export const buildWorkspaceParams = () => {
  const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
  
  const params: { workspace?: string; teamId?: string } = {};
  
  if (currentWorkspace.type === 'personal') {
    params.workspace = 'personal';
  } else if (currentWorkspace.type === 'team' && currentWorkspace.teamId) {
    params.workspace = 'team';
    params.teamId = currentWorkspace.teamId;
  }
  
  return params;
};